import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import type { GADashboardData, GASubTab } from './gaAnalyticsTypes';

export function useAdminGAAnalytics() {
  const [data, setData] = useState<GADashboardData>({
    overview: null,
    traffic: null,
    pages: null,
    funnel: null,
    trends: null,
    demographics: null,
    engagement: null,
    errors: null,
  });
  const [loading, setLoading] = useState(true);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});
  const [timeRange, setTimeRange] = useState(30);
  const [activeSubTab, setActiveSubTab] = useState<GASubTab>('overview');
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchAll = useCallback(async (clearCache = false) => {
    setLoading(true);
    setSectionErrors({});

    if (clearCache) {
      try {
        await api.post('/api/admin/ga/refresh-cache');
      } catch {
        // cache clear is best-effort
      }
    }

    const days = timeRange;
    const errors: Record<string, string> = {};

    const [overview, traffic, pages, funnel, trends, demographics, engagement, errorsData] =
      await Promise.allSettled([
        api.get(`/api/admin/ga/overview?days=${days}`),
        api.get(`/api/admin/ga/traffic?days=${days}`),
        api.get(`/api/admin/ga/pages?days=${days}`),
        api.get(`/api/admin/ga/funnel?days=${days}`),
        api.get(`/api/admin/ga/trends?days=${days}`),
        api.get(`/api/admin/ga/demographics?days=${days}`),
        api.get(`/api/admin/ga/engagement?days=${days}`),
        api.get(`/api/admin/ga/errors?days=${days}`),
      ]);

    const extract = (result: PromiseSettledResult<any>, key: string) => {
      if (result.status === 'fulfilled') return result.value.data;
      errors[key] = result.reason?.response?.data?.detail || result.reason?.message || 'Failed to load';
      return null;
    };

    setData({
      overview: extract(overview, 'overview'),
      traffic: extract(traffic, 'traffic'),
      pages: extract(pages, 'pages'),
      funnel: extract(funnel, 'funnel'),
      trends: extract(trends, 'trends'),
      demographics: extract(demographics, 'demographics'),
      engagement: extract(engagement, 'engagement'),
      errors: extract(errorsData, 'errors'),
    });

    setSectionErrors(errors);
    setLastRefreshed(new Date());
    setLoading(false);
  }, [timeRange]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const refresh = useCallback(() => fetchAll(true), [fetchAll]);

  return {
    data,
    loading,
    sectionErrors,
    timeRange,
    setTimeRange,
    activeSubTab,
    setActiveSubTab,
    lastRefreshed,
    refresh,
  };
}
